import React, { useState, useRef, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { getRequest, pathname } from "@react/helpers/fetch"
import Modal      from "@react/shared/ui/Modal"
import Input      from "@react/shared/ui/Input"
import IconBadge  from "@react/shared/ui/IconBadge"
import EmptyState from "@react/shared/ui/EmptyState"

// Search-and-insert modal for adding a single product ("article") as a
// component line. The autocomplete is used only to pick a product (id + name);
// its full pricing payload is fetched from BuildProduct on selection and
// handed back through onSelect.
const AddArticleModal = ({ isOpen, onClose, clientId, onSelect }) => {
  const [query,       setQuery]       = useState("")
  const [results,     setResults]     = useState([])
  const [loading,     setLoading]     = useState(false)
  const [selectingId, setSelectingId] = useState(null)

  const searchRef    = useRef(null)
  const debounceRef  = useRef(null)
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!isOpen) return
    setQuery("")
    setResults([])
    const timer = setTimeout(() => searchRef.current?.focus(), 60)
    return () => clearTimeout(timer)
  }, [isOpen])

  const runSearch = value => {
    setQuery(value)
    clearTimeout(debounceRef.current)
    if (!value || value.trim().length < 2) {
      setResults([])
      return
    }
    debounceRef.current = setTimeout(() => {
      const requestId = ++requestIdRef.current
      const params    = { q: value.trim() }
      if (clientId) params.client = clientId
      setLoading(true)
      getRequest(pathname({ path: "/products/autocomplete", format: "json", params }))
        .then(data => {
          if (requestId !== requestIdRef.current) return
          setResults(Array.isArray(data) ? data : [])
        })
        .finally(() => { if (requestId === requestIdRef.current) setLoading(false) })
    }, 250)
  }

  const choose = product => {
    if (selectingId) return
    setSelectingId(product.id)
    const params = { line_type: "Qline" }
    if (clientId) params.client = clientId
    getRequest(pathname({ path: `/quotes/form/${product.id}/product`, format: "json", params }))
      .then(data => { onSelect(data); onClose() })
      .finally(() => setSelectingId(null))
  }

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      size    = "md"
      eyebrow = {I18n.t("quotes.builder.add_article_modal.eyebrow")}
      title   = {I18n.t("quotes.builder.add_article_modal.title")}
    >
      <Input
        ref          = {searchRef}
        variant      = "search"
        size         = "lg"
        leadingIcon  = "search"
        type         = "text"
        value        = {query}
        onChange     = {e => runSearch(e.target.value)}
        placeholder  = {I18n.t("quotes.builder.add_article_modal.search_placeholder")}
        autoComplete = "off"
      />

      <div className="mt-3 min-h-[260px]">
        {results.map(product => (
          <div
            key       = {product.id}
            onClick   = {() => choose(product)}
            className = "flex items-center gap-3 px-2.5 py-2.5 cursor-pointer border-t border-slate-100 first:border-t-0 hover:bg-slate-100"
          >
            <IconBadge tone="product" size="md">
              <FontAwesomeIcon icon="box" />
            </IconBadge>
            <div className="min-w-0 flex-1 font-medium text-slate-900 text-[13px] whitespace-nowrap overflow-hidden text-ellipsis">
              {product.name}
            </div>
            {selectingId === product.id && <FontAwesomeIcon icon="spinner" spin className="text-slate-400" />}
          </div>
        ))}

        {loading && (
          <EmptyState size="lg"><FontAwesomeIcon icon="spinner" spin /></EmptyState>
        )}
        {!loading && results.length === 0 && (
          <EmptyState size="lg">
            {query.trim().length >= 2
              ? I18n.t("quotes.builder.add_article_modal.no_results")
              : I18n.t("quotes.builder.add_article_modal.help")}
          </EmptyState>
        )}
      </div>
    </Modal>
  )
}

export default AddArticleModal
